import {
    getFocusStyle,
    HighContrastSelector,
    getHighContrastNoAdjustStyle,
    getRTL,
} from '@fluentui/react';
import { IPinActionButtonStyleProps, IPinActionButtonStyles } from './PinActionButton.types';

const isSafari = /apple/i.test(navigator.vendor);

function getIconTransform(isPressed: boolean, isRTL: boolean): string | undefined {
    if (isPressed) {
        return isRTL ? 'scaleX(-0.75) scaleY(0.75)' : 'scale(0.75)';
    }

    if (isRTL) {
        return 'scaleX(-1)';
    }

    return undefined;
}

export const getStyles = (props: IPinActionButtonStyleProps): Partial<IPinActionButtonStyles> => {
    const { theme, isPremium, isHover, isPressed, isVisible, disabled, canInteract = true } = props;

    return {
        root: [
            canInteract &&
                getFocusStyle(theme, {
                    highContrastStyle: {
                        outline: disabled ? '2px dotted GrayText' : '2px dashed ButtonText',
                        ...getHighContrastNoAdjustStyle(),
                    },
                }),
            {
                width: 32,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isPremium
                    ? theme.semanticColors.inputFocusBorderAlt
                    : theme.semanticColors.bodyText,
                [HighContrastSelector]: [
                    {
                        color: 'ButtonText',
                        backgroundColor: 'transparent',
                        ...getHighContrastNoAdjustStyle(),
                    },
                    !disabled &&
                        canInteract && {
                            ':focus': {
                                color: 'HighlightText',
                                backgroundColor: 'Highlight',
                            },
                        },
                ],
            },
            isHover &&
                canInteract && {
                    backgroundColor: theme.semanticColors.menuItemBackgroundChecked,
                    [HighContrastSelector]: {
                        zIndex: 1, // To force the outline to overlap everything
                        color: 'HighlightText',
                        backgroundColor: 'Highlight',
                        outline: '2px dashed ButtonText',
                        ...getHighContrastNoAdjustStyle(),
                    },
                },
            isPressed &&
                canInteract && {
                    backgroundColor: theme.palette.neutralQuaternary,
                    [HighContrastSelector]: {
                        zIndex: 1, // To force the outline to overlap everything
                        color: 'HighlightText',
                        backgroundColor: 'Highlight',
                        outline: '2px solid ButtonText',
                        ...getHighContrastNoAdjustStyle(),
                    },
                },
        ],
        iconWrapper: [
            {
                opacity: isVisible ? 1 : 0,
                transition: `opacity ${isSafari ? 0 : 100}ms`,
                height: '20px',
                width: '20px',
            },
        ],
        icon: [
            {
                height: '100%',
                width: '100%',
                transition: `transform ${isSafari ? 0 : 200}ms cubic-bezier(0.33, 0, 0.1, 1.0)`,
                transform: getIconTransform(isPressed && canInteract, getRTL(theme)),
            },
            disabled && {
                opacity: 0.4,
                selectors: {
                    [HighContrastSelector]: {
                        opacity: 1,
                    },
                },
            },
        ],
    };
};
